import { useToast } from '@lifebit-ai/lds'
import { useQueryClient } from '@tanstack/react-query'
import { ResultsState } from 'enums'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { extractErrorMessage } from 'core/utils'
import { useS3BrowserV2 } from 'hooks/useS3BrowserV2'
import { getExportAnalysisListPath, getExportFilesListPath } from 'modules/requests/requestDetailPaths'
import {
  RESULT_STATUS_REFETCH_INTERVAL_MS,
  getExportRequestName,
  isDataAccessRequest,
  shouldPollResultStatus
} from 'modules/requests/utils'
import { DownloadExpiryKeys } from 'modules/resultsV2/enums'
import { validateResultLabel } from 'modules/resultsV2/labelValidation'
import {
  DATA_ACCESS_REQUEST_APPROVAL_SUCCESS_MESSAGE,
  EXPORT_REQUEST_APPROVAL_SUCCESS_MESSAGE,
  downloadExpiryOptions,
  getWorkspaceDownloadExpiryOptionKey,
  isAppealRequest,
  isReRequest,
  isResultDownloadLinkExpired
} from 'modules/resultsV2/utils'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import { useCreateResultLabel } from 'services/results/hooks/useCreateResultLabel'
import { useDeleteResultLabel } from 'services/results/hooks/useDeleteResultLabel'
import { useGetLabelsFromExportRequests } from 'services/results/hooks/useGetLabelsFromExportRequests'
import { useRejectResult } from 'services/results/hooks/useRejectResult'
import { useRequestAttestation } from 'services/results/hooks/useRequestAttestation'
import { ResultsQueryKeys } from 'services/results/queryKeys'
import { Result, ResultRequestType } from 'services/results/types'
import { useApproveResultV2 } from 'services/results/v2/hooks/useApproveResultV2'
import { useGetUserInfo } from 'services/user/hooks/useGetUserInfo'
import { useGetWorkspaceConfiguration } from 'services/workspaces/v2/hooks/useGetWorkspaceConfiguration'
import {
  getWorkspaceConfigurationDownloadLinkExpiresInSeconds,
  hasWorkspaceConfiguration,
  isAttestationEnabledForActivePolicy,
  isExportRequestFileViewDisabled
} from 'services/workspaces/v2/utils'
import {
  getAdditionalInfoRequestedSuccessToast,
  getRequestAdditionalInfoFailedToast
} from 'utils/additionalInfoFormCopy'

const useResults = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<string>('tab1')
  const { resultId: legacyResultId, requestId } = useParams<{ resultId?: string; requestId?: string }>()
  const resultId = legacyResultId ?? requestId
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState<boolean>(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false)
  const [isAttestationModalOpen, setIsAttestationModalOpen] = useState<boolean>(false)

  const [selectedDownloadExpiryOptionKey, setSelectedDownloadExpiryOptionKey] = useState<DownloadExpiryKeys>(
    DownloadExpiryKeys.SevenDays
  )

  const { data: userInfo } = useGetUserInfo()

  const { result, isLoading, breadcrumbs, contents, openDir, goUpOneLevel, reports, bucket, prefix, provider, isFile } =
    useS3BrowserV2({
      resultId: resultId as string,
      getResultDetailsOptions: {
        refetchInterval: query =>
          shouldPollResultStatus(query.state.data?.status) ? RESULT_STATUS_REFETCH_INTERVAL_MS : false
      }
    })

  const resultRef = useRef(result)
  resultRef.current = result

  const workspaceConfigurationQuery = useGetWorkspaceConfiguration(result?.workspaceId ?? '', {
    enabled: !!result?.workspaceId
  })

  const labelsQuery = useGetLabelsFromExportRequests()

  const createResultLabelMutation = useCreateResultLabel({
    onSuccess: (_, variables) => {
      toast({ message: 'Label added successfully' })
      queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.LabelsFromExportRequests() })
      queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(variables.resultId) })
      invalidateRequestActivityQueries(queryClient)
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to add label') })
    }
  })

  const deleteResultLabelMutation = useDeleteResultLabel(resultId as string, {
    onSuccess: () => {
      toast({ message: 'Label removed successfully' })
      queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.LabelsFromExportRequests() })

      if (resultId) {
        queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(resultId) })
      }

      invalidateRequestActivityQueries(queryClient)
    }
  })

  const workspaceConfiguration = workspaceConfigurationQuery.data
  const downloadLinkExpiresInSeconds = getWorkspaceConfigurationDownloadLinkExpiresInSeconds(workspaceConfiguration)

  useEffect(() => {
    if (workspaceConfigurationQuery.isSuccess && downloadLinkExpiresInSeconds) {
      const workspaceDownloadExpiryOptionKey = getWorkspaceDownloadExpiryOptionKey(downloadLinkExpiresInSeconds)
      if (workspaceDownloadExpiryOptionKey === null) return

      setSelectedDownloadExpiryOptionKey(workspaceDownloadExpiryOptionKey)
    }
  }, [workspaceConfigurationQuery.isSuccess, downloadLinkExpiresInSeconds])

  const isReadyForDownload: boolean = !!(resultId && bucket && prefix)
  const isAnalysis = !!result?.resourceTypeId
  const isDataAccessRequestResult = !!result && isDataAccessRequest(result)
  const isUserTheRequester = userInfo?._id === result?.requestingUser?._id
  const showMeta = !!result && !isAnalysis

  const isApprovalRestricted =
    isUserTheRequester &&
    !!(
      hasWorkspaceConfiguration(workspaceConfiguration) &&
      workspaceConfiguration.permissions.admins?.enableSelfReviewRestriction
    )

  // File access is permission-gated, so keep the real actions hidden until the workspace
  // configuration resolves. A failed configuration stays hidden; only the in-flight request
  // gets a skeleton in the actions slot. The admin file view setting only applies to export requests.
  const isWorkspaceConfigurationPending = !!result?.workspaceId && !workspaceConfigurationQuery.isSuccess
  const isWorkspaceConfigurationLoading = isWorkspaceConfigurationPending && !workspaceConfigurationQuery.isError
  const isFileViewDisabled =
    !isAnalysis && (isWorkspaceConfigurationPending || isExportRequestFileViewDisabled(workspaceConfiguration))
  const isFileActionsLoading = !isAnalysis && isWorkspaceConfigurationLoading
  const isParticipantFileDownloadDisabled =
    isApprovalRestricted || (isUserTheRequester && isWorkspaceConfigurationPending)
  const isParticipantFileDownloadLoading = isUserTheRequester && isWorkspaceConfigurationLoading

  const isPendingVerification = result?.status === ResultsState.PendingVerification
  const isApprovalActionsVisible = isPendingVerification && !isApprovalRestricted
  const showRejectButton = isApprovalActionsVisible && !isAnalysis

  const showAttestationButton =
    result?.requestType === ResultRequestType.ExportRequest &&
    isAttestationEnabledForActivePolicy(workspaceConfiguration)

  const showPreviousApprovalDetails = !!result && isReRequest(result)

  const showPreviousAppealRejectedRequestDetails =
    !!result && isAppealRequest(result) && result?.appealReview?.previousRequest?.status === ResultsState.Rejected

  const showPreviousAttestationRejectedRequestDetails =
    !!result &&
    (result.attestationReview?.previousRequests?.some(
      previousRequest => previousRequest.status === ResultsState.Rejected
    ) ||
      result.attestationReview?.previousRequest?.status === ResultsState.Rejected)

  const approvalModalTitle = isAnalysis ? 'Approve analysis results export?' : 'Approve data export request?'
  const rejectionModalTitle = isDataAccessRequestResult ? 'Reject data access request?' : 'Reject data export request?'

  const rejectionModalDescription = isDataAccessRequestResult
    ? `The request from ${result?.requestingUser?.email} (${result?.workspaceName}) will be rejected. Enter a reason for the rejection, it will be visible to the requester and to other Airlock Managers.`
    : `Do you want to reject the request from ${result?.requestingUser?.email} (${result?.workspaceName}) to export the data?`

  const rejectionModalOkTitle = isDataAccessRequestResult ? 'Reject access' : 'Reject request'

  const downloadLinkTtlSeconds =
    result?.downloadLinkExpiresInSeconds ??
    getWorkspaceConfigurationDownloadLinkExpiresInSeconds(workspaceConfiguration)

  const onClickReject = () => {
    if (!resultId || isApprovalRestricted) return

    setIsRejectModalOpen(true)
  }

  const onClickApprove = () => {
    if (!resultId || isApprovalRestricted) return

    if (isDataAccessRequestResult) {
      if (approveResultV2Mutation.isPending) return

      approveResultV2Mutation.mutate({
        resultId,
        params: {}
      })
      return
    }

    setIsApprovalModalOpen(true)
  }

  const onClickRequestAttestation = () => {
    if (!resultId || isApprovalRestricted) return
    setIsAttestationModalOpen(true)
  }

  const onClickBack = () => {
    if (!resultId) return

    navigate(isAnalysis ? getExportAnalysisListPath() : getExportFilesListPath())
  }

  const approveResultV2Mutation = useApproveResultV2({
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to approve result') })
    },
    onSuccess: (_data, variables) => {
      const resultDetails = queryClient.getQueryData<Result>(ResultsQueryKeys.ResultDetails(variables.resultId))
      const approvedResult = resultDetails ?? resultRef.current
      const isApprovedDataAccessRequest = !!approvedResult && isDataAccessRequest(approvedResult)

      toast({
        message: isApprovedDataAccessRequest
          ? DATA_ACCESS_REQUEST_APPROVAL_SUCCESS_MESSAGE
          : EXPORT_REQUEST_APPROVAL_SUCCESS_MESSAGE,
        type: 'success'
      })

      invalidateRequestActivityQueries(queryClient)

      // A data access request approval doesn't produce a download, so refetch its real
      // status instead of optimistically flipping it to Processing (an export-only state).
      if (isApprovedDataAccessRequest) {
        queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(variables.resultId) })
        return
      }

      if (!resultDetails) return

      queryClient.setQueryData<Result>(ResultsQueryKeys.ResultDetails(variables.resultId), {
        ...resultDetails,
        status: ResultsState.Processing
      })
    },
    onSettled: () => {
      setIsApprovalModalOpen(false)
    }
  })

  const rejectResultMutation = useRejectResult(resultId as string, {
    onSuccess: () => {
      if (resultId) {
        queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(resultId) })
      }

      invalidateRequestActivityQueries(queryClient)
      setIsRejectModalOpen(false)
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to reject result') })
    }
  })

  const requestAttestationMutation = useRequestAttestation(resultId as string, {
    onSuccess: () => {
      toast({ message: getAdditionalInfoRequestedSuccessToast() })

      if (resultId) {
        queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(resultId) })
      }

      invalidateRequestActivityQueries(queryClient)
      setIsAttestationModalOpen(false)
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, getRequestAdditionalInfoFailedToast()) })
    }
  })

  const getTitle = (): string => {
    if (isAnalysis) return result?.description || '--'
    return getExportRequestName(result)
  }

  const handleOnClickRetryApproveResult = (result: Result) => (): void => {
    if (approveResultV2Mutation.isPending) return

    const workspaceDownloadExpiryOptionKey = hasWorkspaceConfiguration(workspaceConfiguration)
      ? getWorkspaceDownloadExpiryOptionKey(workspaceConfiguration.downloadLinkExpiresInSeconds)
      : undefined

    if (!workspaceDownloadExpiryOptionKey) {
      toast({ message: 'Workspace download expiry duration not set' })
      return
    }

    approveResultV2Mutation.mutate({
      resultId: result._id,
      params: { downloadLinkExpiresInSeconds: downloadExpiryOptions[workspaceDownloadExpiryOptionKey].timeInSeconds }
    })
  }

  const handleApproveResult = (): void => {
    if (!resultId || approveResultV2Mutation.isPending) return

    approveResultV2Mutation.mutate({
      resultId: resultId,
      params: {
        downloadLinkExpiresInSeconds: downloadExpiryOptions[selectedDownloadExpiryOptionKey].timeInSeconds
      }
    })
  }

  const handleRequestAttestation = (): void => {
    if (!resultId) return

    requestAttestationMutation.mutate()
  }

  const handleAddResultLabel = (name: string): void => {
    if (!resultId) return

    const trimmedName = name.trim()
    if (!trimmedName) return

    createResultLabelMutation.mutate({ resultId, name: trimmedName })
  }

  const handleDeleteResultLabel = (labelId: string): void => {
    if (!resultId) return

    const trimmedLabelId = labelId.trim()
    if (!trimmedLabelId) return

    deleteResultLabelMutation.mutate({ labelId: trimmedLabelId })
  }

  const handleOnClickLabelMenuItem = (labelId: string): void => {
    const selectedLabel = labelsQuery.data?.data.find(label => label._id === labelId)
    if (!selectedLabel) return

    handleAddResultLabel(selectedLabel.name)
  }

  const closeApprovalModal = () => setIsApprovalModalOpen(false)
  const closeRejectModal = () => setIsRejectModalOpen(false)
  const closeAttestationModal = () => setIsAttestationModalOpen(false)

  return {
    resultId,
    result,
    isLoading,
    breadcrumbs,
    contents,
    openDir,
    goUpOneLevel,
    reports,
    bucket,
    prefix,
    provider,
    isFile,
    isAnalysis,
    isDataAccessRequestResult,
    showMeta,
    isReadyForDownload,
    isApprovalRestricted,
    isFileViewDisabled,
    isFileActionsLoading,
    isParticipantFileDownloadDisabled,
    isParticipantFileDownloadLoading,
    isApprovalActionsVisible,
    showRejectButton,
    showAttestationButton,
    showPreviousApprovalDetails,
    showPreviousAppealRejectedRequestDetails,
    showPreviousAttestationRejectedRequestDetails,
    isUserTheRequester,
    isDownloadLinkExpired:
      !!result && !!downloadLinkTtlSeconds ? isResultDownloadLinkExpired(result, downloadLinkTtlSeconds) : false,
    activeTab,
    setActiveTab,
    isApprovalModalOpen,
    isRejectModalOpen,
    isAttestationModalOpen,
    selectedDownloadExpiryOptionKey,
    setSelectedDownloadExpiryOptionKey,
    onClickReject,
    onClickApprove,
    onClickRequestAttestation,
    onClickBack,
    getTitle,
    approvalModalTitle,
    rejectionModalTitle,
    rejectionModalDescription,
    rejectionModalOkTitle,
    handleApproveResult,
    handleRequestAttestation,
    handleOnClickRetryApproveResult,
    closeApprovalModal,
    closeRejectModal,
    closeAttestationModal,
    approveResultV2Mutation,
    rejectResultMutation,
    requestAttestationMutation,
    createResultLabelMutation,
    deleteResultLabelMutation,
    workspaceConfigurationQuery,
    labelsQuery,
    validateResultLabel,
    handleOnClickLabelMenuItem,
    handleAddResultLabel,
    handleDeleteResultLabel
  }
}

export default useResults
